import {closeCurrentDialog, openDialog} from 'platform/components';

import {
  useGetCheckoutAllowedCustomerQuery,
  useGetCheckoutBillingInformationQuery,
  usePutCheckoutSetBillingInformationMutation,
  usePutCheckoutSetContractInformationMutation,
} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {CustomerMatchOrCreate, handleApiError} from '@dms/shared';

import {Nullish} from 'shared/web';

import {getAllowedCustomerLabel} from '../utils/getAllowedCustomerLabel';

export function useBillingInformation(checkoutId: string) {
  const {
    data: allowedCustomers,
    isLoading: areAllowedCustomersLoading,
    isError: areAllowedCustomersError,
  } = useGetCheckoutAllowedCustomerQuery({checkoutId});

  const {
    data: billingInformation,
    isLoading: isBillingInformationLoading,
    isError: isBillingInformationError,
  } = useGetCheckoutBillingInformationQuery({checkoutId});

  const [putCheckoutSetBillingInformation, {isLoading: isSettingBillingInformationLoading}] =
    usePutCheckoutSetBillingInformationMutation();
  const [putCheckoutSetContractInformation] = usePutCheckoutSetContractInformationMutation();

  const handleChangeContractInformation = (customerContractInformationId: string) =>
    putCheckoutSetContractInformation({
      checkoutId,
      body: {customerContractInformationId},
    })
      .unwrap()
      .catch(handleApiError);

  const handleChangeBillingRecipient = (values: string[] | Nullish) => {
    if (values?.includes('OTHER')) {
      return openDialog(
        <CustomerMatchOrCreate
          onCustomer={(customer) =>
            putCheckoutSetBillingInformation({
              checkoutId,
              body: {billingRecipient: 'OTHER', customerId: customer.id},
            })
              .unwrap()
              .then(closeCurrentDialog)
              .catch(handleApiError)
          }
        />,
        {title: i18n.t('entity.customer.labels.customer')}
      );
    }

    const selectedCustomer = allowedCustomers?.find((customer) =>
      values?.includes(customer.billingRecipient)
    );

    if (selectedCustomer) {
      putCheckoutSetBillingInformation({checkoutId, body: selectedCustomer})
        .unwrap()
        .catch(handleApiError);
    }
  };

  const allowedCustomerOptions =
    allowedCustomers?.map((customer) => ({
      label: getAllowedCustomerLabel(customer.billingRecipient),
      value: customer.billingRecipient,
    })) ?? [];

  const isLoading = isBillingInformationLoading || areAllowedCustomersLoading;

  const isError = isBillingInformationError || areAllowedCustomersError;

  return {
    isLoading,
    isError,
    allowedCustomerOptions,
    billingInformation,
    isSettingBillingInformationLoading,
    handleChangeBillingRecipient,
    handleChangeContractInformation,
  };
}
